import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, Upload, X, Trash2, Eye, EyeOff, Image as ImageIcon, ZoomIn, ZoomOut, ChevronDown, ChevronRight, Check, Zap, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { Connection, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { useActivity } from '../contexts/ActivityContext';
import { useSolPrice } from '../contexts/SolPriceContext';
import { useWallet } from '../contexts/WalletContext';
import { useWallet as useSolanaWallet } from '@solana/wallet-adapter-react';
import WalletRequired from '../components/WalletRequired';
import { createPiece } from '../utils/supabaseDatabase.js';
import { getExplorerUrl } from '../utils/solanaMinting.js';
import Logo3D from '../components/Logo3D';

const CreatePiece = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const { logPieceCreated } = useActivity();
  const { solPrice } = useSolPrice(); // Get real-time SOL price from global context
  const { publicKey } = useWallet();
  const solanaWallet = useSolanaWallet(); // Get the full Solana wallet adapter
  const [isCreating, setIsCreating] = useState(false);
  const [pieceModel, setPieceModel] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [otherCategory, setOtherCategory] = useState('');
  const [filePreview, setFilePreview] = useState(null);
  const [modelViewerKey, setModelViewerKey] = useState(0);
  const canvasRef = useRef(null);
  
  // Minting progress modal state (like execute trading page)
  const [mintProgressModal, setMintProgressModal] = useState({
    show: false,
    step: 0,
    totalSteps: 2,
    currentMessage: 'Initializing minting...',
    isComplete: false,
    mintAddress: '',
    mintSignature: '',
    error: null
  });
  
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [mintResult, setMintResult] = useState(null);
  
  // Minting error modal state
  const [showMintErrorModal, setShowMintErrorModal] = useState(false);
  const [mintErrorDetails, setMintErrorDetails] = useState({
    totalMinted: 0,
    totalFailed: 0,
    quantity: 0,
    errors: []
  });
  
  // Notification system
  const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    color: '',
    price: '',
    supply: '',
    website: '',
    twitter: '',
    discord: '',
    instagram: '',
    royaltyPercent: '0' // Default 0% royalty
  });
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [pieceTags, setPieceTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const categoryDropdownRef = useRef(null);
  const [walletBalance, setWalletBalance] = useState(null);

  const categories = [
    "Tops", "Bottoms", "Shoes", "Accessories", "Other"
  ];

  // Handle click outside category dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setIsCategoryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch wallet balance
  useEffect(() => {
    const fetchBalance = async () => {
      if (publicKey && solanaWallet.connected) {
        try {
          // Use Helius mainnet RPC endpoint for better reliability
          const heliusKey = import.meta.env.VITE_HELIUS_API_KEY;
          const rpcEndpoint = heliusKey 
            ? `https://mainnet.helius-rpc.com/?api-key=${heliusKey}`
            : 'https://api.mainnet-beta.solana.com';
          
          const connection = new Connection(
            rpcEndpoint,
            'confirmed'
          );
          const balance = await connection.getBalance(publicKey);
          const balanceSOL = balance / LAMPORTS_PER_SOL;
          setWalletBalance(balanceSOL);
          console.log('💰 Wallet balance (mainnet):', balanceSOL.toFixed(4), 'SOL');
        } catch (error) {
          console.error('Error fetching wallet balance:', error);
          setWalletBalance(null);
        }
      } else {
        setWalletBalance(null);
      }
    };

    fetchBalance();
    // Refresh balance every 30 seconds
    const interval = setInterval(fetchBalance, 30000);
    
    return () => clearInterval(interval);
  }, [publicKey, solanaWallet.connected]);

  // Helper function to show notifications
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 5000);
  };

  // Check if localStorage has enough space
  const checkStorageSpace = (dataSize) => {
    try {
      const testKey = '__storage_test__';
      const testData = 'x'.repeat(dataSize);
      localStorage.setItem(testKey, testData);
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.category || !formData.color || !formData.description || !formData.price || !formData.supply) {
      showNotification('Please fill in all required fields including price and quantity', 'error');
      return;
    }

    // Check if all social links are filled
    if (!formData.website || !formData.twitter || !formData.instagram) {
      showNotification('Please fill in all social links (Website, X/Twitter, and Instagram)', 'error');
      return;
    }

    // Validate quantity
    const quantity = parseInt(formData.supply) || 1;
    if (quantity < 1 || quantity > 50) {
      showNotification('Quantity must be between 1 and 50', 'error');
      return;
    }

    // Validate royalty percentage
    const royaltyPct = Number(formData.royaltyPercent || 0);
    if (!Number.isFinite(royaltyPct) || royaltyPct < 0 || royaltyPct > 10) {
      showNotification('Creator royalties must be between 0% and 10%', 'error');
      return;
    }

    if (!publicKey) {
      console.error('Wallet not connected - publicKey is:', publicKey);
      showNotification('Please connect your wallet to create a piece', 'error');
      return;
    }

    if (!solanaWallet.connected || !solanaWallet.publicKey) {
      showNotification('Please connect your Solana wallet to mint your piece', 'error');
      return;
    }

    console.log('Wallet connected - publicKey:', publicKey.toString());
    console.log('Solana wallet connected:', solanaWallet.publicKey.toString());
    
    // 3D model is mandatory
    if (!pieceModel) {
      showNotification('Please upload a 3D model for your piece', 'error');
      return;
    }

    // Thumbnail is mandatory
    if (!thumbnail) {
      showNotification('Please upload a thumbnail image for your piece', 'error');
      return;
    }

    setIsCreating(true);

    try {
      console.log('Starting piece creation process...');
      
      // Convert royalty percentage to basis points
      const royaltyBps = Math.round(royaltyPct * 100);
      
      // Prepare metadata for Supabase
      const metadata = {
        name: formData.name,
        description: formData.description,
        category: formData.category === 'Other' ? otherCategory.trim() : formData.category,
        color: formData.color,
        price: parseFloat(formData.price),
        quantity: parseInt(formData.supply),
        totalMade: parseInt(formData.supply),
        brand: 'User Created',
        rarity: 'Common',
        tags: pieceTags,
        royaltyBps: royaltyBps,
        // Include piece-specific social links
        socialLinks: {
          website: formData.website || '',
          twitter: formData.twitter || '',
          discord: formData.discord || '',
          instagram: formData.instagram || ''
        }
      };

      console.log('Metadata prepared:', metadata);
      console.log('Uploading to Supabase...');
      console.log('Wallet address:', publicKey.toString());
      console.log('3D Model file:', pieceModel);
      console.log('Thumbnail file:', thumbnail);

      // Create piece in Supabase with 3D model storage
      const savedPiece = await createPiece(metadata, pieceModel, publicKey.toString(), thumbnail);
      
      console.log('Piece uploaded successfully:', savedPiece);
      
      // Create the piece object for local storage (for compatibility)
      const newPiece = {
        id: savedPiece.id,
        name: savedPiece.name,
        description: savedPiece.description,
        category: savedPiece.category,
        color: savedPiece.color,
        price: savedPiece.price,
        supply: savedPiece.total_made,
        payoutWallet: publicKey.toString(),
        website: formData.website,
        twitter: formData.twitter,
        discord: formData.discord,
        instagram: formData.instagram,
        tags: savedPiece.tags,

        modelName: pieceModel ? pieceModel.name : 'No model',
        modelSize: pieceModel ? pieceModel.size : 0,
        modelType: pieceModel ? pieceModel.type : '',
        // Note: 3D model files are not stored on server, only metadata
        // Use Supabase URLs
        modelUrl: savedPiece.preview_glb_url,
        thumbnailUrl: savedPiece.thumbnail_url,
        posterUrl: savedPiece.poster_url,
        previewUrl: savedPiece.preview_glb_url,
        createdAt: savedPiece.created_at,
        
        // Store creator information so it's visible regardless of which wallet is viewing
        creator: savedPiece.creators ? {
          id: savedPiece.creators.id,
          wallet: savedPiece.creators.wallet,
          username: savedPiece.creators.handle,
          profilePicture: savedPiece.creators.profile_picture_url,
          isVerified: savedPiece.creators.is_verified,
          bio: savedPiece.creators.bio,
          socialLinks: savedPiece.creators.social_links
        } : null,
        originalCreator: savedPiece.creators ? {
          id: savedPiece.creators.id,
          wallet: savedPiece.creators.wallet,
          username: savedPiece.creators.handle,
          profilePicture: savedPiece.creators.profile_picture_url,
          profile_picture_url: savedPiece.creators.profile_picture_url,
          isVerified: savedPiece.creators.is_verified,
          bio: savedPiece.creators.bio,
          socialLinks: savedPiece.creators.social_links,
          piecesCount: 0 // Will be calculated dynamically
        } : null
      };

      // Note: Pieces are now stored in Supabase only
      // The Created page will fetch pieces directly from Supabase
      // No longer storing in localStorage to prevent duplicates
      
      // Log piece creation activity
      const usdPrice = formData.price ? (parseFloat(formData.price) * solPrice).toFixed(2) : '0.00';
      logPieceCreated({
        id: savedPiece.id,
        name: savedPiece.name,
        collection: savedPiece.category,
        image: savedPiece.thumbnail_url,
        price: formData.price ? `${formData.price} SOL` : 'Not for sale',
        usdPrice: formData.price ? `$${usdPrice}` : 'Not for sale',
        rarity: `#${Math.floor(Math.random() * 10000)}`,
        transactionHash: `tx_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      });
      
      console.log('Piece created successfully:', newPiece);
      
      // Get quantity before showing modal
      const quantity = parseInt(formData.supply) || 1;
      console.log(`🎨 Will mint ${quantity === 1 ? 'single piece' : `${quantity} pieces`} to Solana...`);
      
      // Show progress modal with quantity info
      setMintProgressModal({
        show: true,
        step: 1,
        totalSteps: 2,
        currentMessage: quantity > 1 
          ? `Creating metadata for ${quantity} pieces...` 
          : 'Creating piece metadata...',
        isComplete: false,
        mintAddress: '',
        mintSignature: '',
        error: null
      });
      
      // Immediately mint the piece as NFT (edition-aware)
      console.log(`🎨 Minting ${quantity === 1 ? 'single piece' : `${quantity} pieces`} to Solana...`);
      
      const pieceData = {
        name: savedPiece.name,
        description: savedPiece.description || `${savedPiece.name} - 3D wearable piece`,
        image: savedPiece.poster_url || savedPiece.thumbnail_url,
        animation_url: savedPiece.preview_glb_url,
        category: savedPiece.category,
        price: savedPiece.price,
        attributes: [
          { trait_type: 'Category', value: savedPiece.category },
          { trait_type: 'Color', value: savedPiece.color || 'N/A' },
          { trait_type: 'Brand', value: savedPiece.brand || 'User Created' },
          // Add tags as a comma-separated string attribute
          ...(savedPiece.tags && savedPiece.tags.length > 0 ? [
            { trait_type: 'Tags', value: savedPiece.tags.join(', ') }
          ] : [])
        ],
        sellerFeeBps: savedPiece.royalty_bps || 500
      };

      // Step 2: Minting the NFT
      setMintProgressModal(prev => ({
        ...prev,
        step: 2,
        currentMessage: quantity > 1 ? `Minting ${quantity} pieces...` : 'Minting NFT on blockchain...'
      }));

      // Import edition minting functions
      const { mintEditions, mintSinglePiece } = await import('../utils/editionMinting.js');

      let mintResult;
      
      if (quantity > 1) {
        // Multi-piece minting (each piece is independent with its own mint_address)
        console.log(`🎨 Minting ${quantity} independent pieces...`);
        console.log('🔒 Disabling mint button to prevent double-submission...');
        
        mintResult = await mintEditions(
          solanaWallet,
          pieceData,
          quantity,
          savedPiece.id,
          (pieceNum, total, result) => {
            console.log(`✅ Minted piece ${pieceNum}/${quantity}`);
            // Update progress message - always show the requested quantity
            setMintProgressModal(prev => ({
              ...prev,
              currentMessage: `Minting piece ${pieceNum}/${quantity}...`
            }));
          }
        );
        
        console.log('✅ Multiple piece minting complete:', {
          requested: quantity,
          totalMinted: mintResult.totalMinted,
          totalFailed: mintResult.totalFailed
        });
        
        // Validate that all requested pieces were minted
        if (mintResult.totalMinted !== quantity) {
          console.error(`❌ Minting quantity mismatch! Requested: ${quantity}, Minted: ${mintResult.totalMinted}`);
        }
        
        // Show warning if some pieces failed to mint
        if (mintResult.totalFailed > 0 || mintResult.totalMinted !== quantity) {
          const actualFailed = quantity - mintResult.totalMinted;
          console.warn(`⚠️ ${actualFailed} pieces failed to mint`);
          
          // Show custom UI error modal instead of browser alert
          setMintErrorDetails({
            totalMinted: mintResult.totalMinted,
            totalFailed: actualFailed,
            quantity: quantity,
            errors: mintResult.errors || []
          });
          setShowMintErrorModal(true);
        }
      } else {
        // Single piece minting (1/1)
        console.log('🎨 Minting single 1/1 piece...');
        console.log('🔒 Disabling mint button to prevent double-submission...');
        
        mintResult = await mintSinglePiece(solanaWallet, pieceData, savedPiece.id);
        console.log('✅ Minting successful:', mintResult);
      }
      
      // Show delegate status to user (for single pieces)
      if (mintResult.delegateActive) {
        console.log('✅ Delegate approval granted - NFT can be sold instantly!');
      } else if (quantity === 1) {
        console.log('⚠️ Delegate approval pending - you\'ll need to approve when listing');
      }
      
      // NOTE: For single pieces, mintSinglePiece already calls updatePieceWithMintAddress internally
      // For multiple pieces, mintEditions already handles updates for each piece
      // No need to call updatePieceWithMintAddress again here!
      
      // Show success in progress modal (or partial success if some failed)
      const wasPartialSuccess = mintResult.totalFailed > 0;
      setMintProgressModal(prev => ({
        ...prev,
        isComplete: true,
        mintAddress: quantity === 1 
          ? mintResult.mintAddress 
          : wasPartialSuccess 
            ? `${mintResult.totalMinted} of ${quantity} pieces (${mintResult.totalFailed} failed)`
            : `${mintResult.totalMinted} pieces`,
        mintSignature: quantity === 1 ? mintResult.signature || mintResult.mintSignature : null,
        error: wasPartialSuccess ? `${mintResult.totalFailed} pieces failed to mint - check console for details` : null
      }));
      
      // Reset form
      setFormData({
        name: '',
        description: '',
        category: '',
        color: '',
        price: '',
        supply: '',
        website: '',
        twitter: '',
        discord: '',
        instagram: '',
        royaltyPercent: '0'
      });
      setPieceModel(null);
      setFilePreview(null);
      setThumbnail(null);
      setThumbnailPreview(null);
      setOtherCategory('');
      setPieceTags([]);
      setNewTag('');
      
    } catch (error) {
      console.error('Error creating/minting piece:', error);
      console.error('Error details:', error.message);
      console.error('Error stack:', error.stack);
      
      // Import isDuplicateSuccess helper
      const { isDuplicateSuccess } = await import('../utils/tx/sendIdempotent');
      
      const msg = String(error?.message ?? error);
      const isDuplicate = error?.__duplicateProcessed || isDuplicateSuccess(error) || msg.includes('already been processed');
      
      // Show error in progress modal
      setMintProgressModal(prev => ({
        ...prev,
        isComplete: false,
        error: error.message || 'Failed to create/mint piece. Please try again.'
      }));
      
      // Handle duplicate transaction as potential success
      if (isDuplicate) {
        showNotification(
          'Minting successful! Redirecting you to view your piece...',
          'success'
        );
        // Navigate to profile so user can check
        setTimeout(() => navigate('/profile/created'), 2000);
      } else if (msg.includes('UNSIGNED_TX')) {
        showNotification('You need to approve the transaction in your wallet to continue.', 'error');
      } else if (msg.includes('no signature') || msg.includes('returned no signature')) {
        showNotification('Transaction could not be completed. Please try again.', 'error');
      } else if (msg.includes('DUPLICATE_NO_RECOVERY')) {
        showNotification(
          'This transaction may have already been processed. Redirecting you to check...',
          'info'
        );
        setTimeout(() => navigate('/profile/created'), 2000);
      } else if (msg.includes('User rejected') || msg.includes('User declined')) {
        showNotification('Minting cancelled. You declined the transaction.', 'info');
      } else if (msg.includes('File size too large') || msg.includes('exceeded the maximum allowed size') || error?.name === 'FileSizeLimitExceeded') {
        const sizeMatch = msg.match(/(\d+\.?\d*)\s*MB/);
        const fileSize = sizeMatch ? sizeMatch[1] : '';
        showNotification(
          `Your file is too large${fileSize ? ' (' + fileSize + ' MB)' : ''}. Please use a file under 50 MB. Try compressing your 3D model or reducing texture quality.`,
          'error'
        );
      } else if (msg.includes('504') || msg.includes('Gateway Timeout') || msg.includes('StorageUnknownError') || msg.includes('not valid JSON')) {
        showNotification(
          'Upload timeout - your file is taking too long to upload. The upload will retry automatically. If this persists, try compressing your 3D model file or check your internet connection.',
          'error'
        );
      } else if (msg.includes('insufficient funds') || msg.includes('Insufficient SOL')) {
        showNotification(
          'Not enough SOL to mint. You need about 0.1 SOL. Get free devnet SOL at solfaucet.com',
          'error'
        );
      } else if (msg.includes('blockhash not found')) {
        showNotification(
          'Network is busy right now. Please wait 30 seconds and try again.',
          'info'
        );
      } else if (msg.includes('mint') || msg.includes('Mint')) {
        showNotification('Unable to mint piece. Make sure you have enough SOL in your wallet.', 'error');
      } else if (msg.includes('delegate') || msg.includes('Delegate')) {
        showNotification(
          'Piece minted successfully! Note: You\'ll need to approve marketplace access when you list this item.',
          'success'
        );
        // Still navigate to profile since mint succeeded
        setTimeout(() => navigate('/profile/created'), 1500);
      } else {
        showNotification('Unable to create piece. Please try again.', 'error');
      }
      
      // Log RPC logs if available for debugging
      if (error?.__rpcLogs) {
        console.error('📋 RPC Logs:', error.__rpcLogs);
      }
    } finally {
      setIsCreating(false);
    }
  };


  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setMintResult(null);
    navigate('/profile/created');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Validate quantity field - prevent values > 50
    if (name === 'supply') {
      const numValue = parseInt(value);
      if (value !== '' && (!isNaN(numValue) && numValue > 50)) {
        showNotification('Maximum quantity is 50 pieces', 'error');
        return; // Don't update state
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle category selection
  const handleCategorySelect = (category) => {
    setFormData(prev => ({
      ...prev,
      category: category
    }));
    setIsCategoryDropdownOpen(false);
  };

  // Tag management functions
  const addTag = () => {
    const tag = (newTag || '').trim().toLowerCase();
    if (tag && !pieceTags.includes(tag) && pieceTags.length < 5) {
      setPieceTags([...pieceTags, tag]);
      setNewTag('');
    } else if (pieceTags.length >= 5) {
      showNotification('You can only add up to 5 tags per piece', 'info');
    }
  };

  const removeTag = (tagToRemove) => {
    setPieceTags(pieceTags.filter(tag => tag !== tagToRemove));
  };

  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (pieceTags.length < 5) {
        addTag();
      }
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log('File selected:', file.name, 'Type:', file.type, 'Size:', file.size);
      
      // Check file size (limit to 50MB)
      if (file.size > 50 * 1024 * 1024) {
        showNotification('File size must be less than 50MB', 'error');
        return;
      }
      
      // Check file type - ONLY GLB files are accepted (not GLTF)
      const fileName = file.name.toLowerCase();
      const isValidType = fileName.endsWith('.glb') || 
                         file.type === 'model/gltf-binary' ||
                         (file.type === '' && fileName.endsWith('.glb')); // Some GLB files have empty MIME type
      
      if (!isValidType) {
        showNotification('Only GLB files are supported! Please upload a .glb file (not GLTF). Selected file: ' + file.name, 'error');
        return;
      }
      
      // Create file preview
      const preview = {
        name: file.name,
        size: file.size,
        type: file.type || 'model/gltf-binary',
        extension: 'GLB',
        lastModified: new Date(file.lastModified).toLocaleDateString(),
        icon: '🎲'
      };
      
      setFilePreview(preview);
      setPieceModel(file);
      setModelViewerKey(prev => prev + 1); // Force re-render of 3D viewer
      console.log('File uploaded successfully:', file.name);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    // Don't show drag feedback if a model is already uploaded
    if (pieceModel) {
      return;
    }
    e.currentTarget.classList.add('border-white', 'bg-[#0a0a0a]');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (pieceModel) {
      return;
    }
    e.currentTarget.classList.remove('border-white', 'bg-[#0a0a0a]');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('border-white', 'bg-[#0a0a0a]');
    
    // Don't allow dropping files if a model is already uploaded
    if (pieceModel) {
      return;
    }
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      console.log('File dropped:', file.name, 'Type:', file.type, 'Size:', file.size);
      
      // Check file size (limit to 50MB)
      if (file.size > 50 * 1024 * 1024) {
        showNotification('File size must be less than 50MB', 'error');
        return;
      }
      
      // Check file type - ONLY GLB files are accepted (not GLTF)
      const fileName = file.name.toLowerCase();
      const isValidType = fileName.endsWith('.glb') || 
                         file.type === 'model/gltf-binary' ||
                         (file.type === '' && fileName.endsWith('.glb')); // Some GLB files have empty MIME type
      
      if (!isValidType) {
        showNotification('Only GLB files are supported! Please upload a .glb file (not GLTF). Selected file: ' + file.name, 'error');
        return;
      }
      
      // Create file preview
      const preview = {
        name: file.name,
        size: file.size,
        type: file.type || 'model/gltf-binary',
        extension: 'GLB',
        lastModified: new Date(file.lastModified).toLocaleDateString(),
        icon: '🎲'
      };
      
      setFilePreview(preview);
      setPieceModel(file);
      setModelViewerKey(prev => prev + 1); // Force re-render of 3D viewer
      console.log('File dropped successfully:', file.name);
    }
  };

  const removeModel = () => {
    setPieceModel(null);
    setFilePreview(null);
    setModelViewerKey(prev => prev + 1); // Force re-render of 3D viewer
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const triggerFileUpload = (e) => {
    e.preventDefault();
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  // Thumbnail upload handlers
  const handleThumbnailUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log('Thumbnail selected:', file.name, 'Type:', file.type, 'Size:', file.size);
      
      // Check file size (limit to 10MB for thumbnails)
      if (file.size > 10 * 1024 * 1024) {
        showNotification('Thumbnail file size must be less than 10MB', 'error');
        return;
      }
      
      // Check file type - only allow image files
      const isValidType = file.type.startsWith('image/');
      
      if (!isValidType) {
        showNotification('Please upload an image file for the thumbnail. Selected file type: ' + file.type, 'error');
        return;
      }
      
      // Reset previous thumbnail state first
      setThumbnail(null);
      setThumbnailPreview(null);
      
      // Create thumbnail preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setThumbnailPreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      setThumbnail(file);
      console.log('Thumbnail uploaded successfully:', file.name);
    } else {
      // If no file selected, reset the state
      setThumbnail(null);
      setThumbnailPreview(null);
    }
  };

  const removeThumbnail = () => {
    setThumbnail(null);
    setThumbnailPreview(null);
    // Clear the file input
    const fileInput = document.getElementById('thumbnail-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleUploadAreaClick = (e) => {
    // Only trigger file upload if no file is uploaded yet
    if (pieceModel) {
      return; // Don't allow clicking the area when a model is already uploaded
    }
    
    // Only trigger file upload if clicking on the upload area itself, not on buttons or other elements
    if (e.target.closest('button') || e.target.closest('input') || e.target.closest('select')) {
      return;
    }
    triggerFileUpload(e);
  };

  // 3D Model Viewer Component
  const ModelViewer = ({ file }) => {
    useEffect(() => {
      if (!file || !canvasRef.current) return;

      // Scene setup
      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x0a0a0a);

      // Camera setup
      const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
      camera.position.set(0, 0, 5);

      // Renderer setup
      const renderer = new THREE.WebGLRenderer({ 
        canvas: canvasRef.current, 
        antialias: true,
        alpha: true 
      });
      renderer.setSize(300, 300);
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;

      // Lighting
      const ambientLight = new THREE.AmbientLight(0xffffff, 1.2);
      scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 1.8);
      directionalLight.position.set(10, 10, 5);
      directionalLight.castShadow = true;
      scene.add(directionalLight);

      const pointLight = new THREE.PointLight(0xffffff, 1.0);
      pointLight.position.set(-10, -10, -5);
      scene.add(pointLight);

      // Additional fill light for better model illumination
      const fillLight = new THREE.DirectionalLight(0xffffff, 1.0);
      fillLight.position.set(-5, 5, 5);
      scene.add(fillLight);

      // Top light to brighten the crown area
      const topLight = new THREE.DirectionalLight(0xffffff, 1.2);
      topLight.position.set(0, 15, 0);
      scene.add(topLight);

      // Controls
      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.enableZoom = true;
      controls.enablePan = false;
      controls.autoRotate = true;
      controls.autoRotateSpeed = 2;

      // Load GLB model
      const loader = new GLTFLoader();
      
      // Handle both File objects and URL strings (for existing models)
      let fileURL;
      if (typeof file === 'object' && file.url && file.isExisting) {
        // This is an existing model from a draft with a URL
        fileURL = file.url;
      } else if (file instanceof File) {
        // This is a new file upload
        fileURL = URL.createObjectURL(file);
      } else {
        // Fallback for other cases
        fileURL = file;
      }
      
      loader.load(
        fileURL,
        (gltf) => {
          const model = gltf.scene;
          
          // Center the model
          const box = new THREE.Box3().setFromObject(model);
          const center = box.getCenter(new THREE.Vector3());
          model.position.sub(center);
          
          // Scale model to fit in view
          const size = box.getSize(new THREE.Vector3());
          const maxDim = Math.max(size.x, size.y, size.z);
          const scale = 2 / maxDim;
          model.scale.setScalar(scale);
          
          // Add shadows and preserve original materials
          model.traverse((child) => {
            if (child.isMesh) {
              child.castShadow = true;
              child.receiveShadow = true;
              
              // Preserve original material properties - DO NOT modify colors or materials
              if (child.material) {
                // Only mark material as needing update, but don't change any properties
                child.material.needsUpdate = true;
                
                // Note: We're preserving the original colors, metalness, roughness, and tone mapping
                // to ensure the model looks exactly as it was designed
              }
            }
          });
          
          scene.add(model);
          
          // Adjust camera to fit model
          const box2 = new THREE.Box3().setFromObject(model);
          const size2 = box2.getSize(new THREE.Vector3());
          const maxDim2 = Math.max(size2.x, size2.y, size2.z);
          camera.position.set(0, 0, maxDim2 * 2);
          camera.lookAt(0, 0, 0);
          controls.update();
        },
        (progress) => {
          console.log('Loading progress:', (progress.loaded / progress.total * 100) + '%');
        },
        (error) => {
          console.error('Error loading model:', error);
        }
      );

      // Animation loop
      const animate = () => {
        requestAnimationFrame(animate);
        controls.update();
        renderer.render(scene, camera);
      };
      animate();

      // Cleanup
      return () => {
        renderer.dispose();
        // Only revoke object URLs for File objects, not for existing URLs
        if (file instanceof File) {
          URL.revokeObjectURL(fileURL);
        }
      };
    }, [file]);

    return (
      <div className="relative">
        <canvas
          ref={canvasRef}
          className="w-full h-[300px] rounded-xl border border-[#2a2a2a] bg-[#0a0a0a]"
        />
      </div>
    );
  };

  return (
    <WalletRequired 
      message="You need to connect your wallet to create and mint pieces."
      action="create pieces"
    >
      <div className="min-h-screen bg-black">
        <div className="max-w-5xl mx-auto px-6 py-12">
          {/* Header */}
          <div className="mb-12 text-center">
            <h1 className="text-3xl font-bold text-white mb-2">
              Create New Piece
            </h1>
          <p className="text-gray-400">
            Design and mint your digital fashion piece with our intuitive creation tools
          </p>
        </div>

        {/* Main Form */}
        <div className="bg-[#111111] rounded-3xl shadow-xl border border-[#1a1a1a] overflow-hidden">
          <div className="px-8 py-6">
            <h2 className="text-xl font-semibold text-white">Piece Information</h2>
            <p className="text-gray-400 mt-1">Fill in the details below to create your piece</p>
          </div>
          
          <form onSubmit={handleSubmit} className="p-8 space-y-8">
            {/* Basic Information */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white border-b border-[#2a2a2a] pb-2">Basic Details</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Piece Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    maxLength={100}
                    className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200"
                    placeholder="Enter piece name"
                    required
                    autoComplete="off"
                    style={{ position: 'relative', zIndex: 10 }}
                  />
                  <p className="text-xs text-gray-400 text-right">
                    {formData.name.length}/100 characters
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Category <span className="text-red-400">*</span>
                  </label>
                  <div className="relative" ref={categoryDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                      className="w-full flex items-center justify-between bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white py-3 px-4 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200 hover:border-[#3a3a3a] cursor-pointer"
                    >
                      <span className={formData.category ? 'text-white' : 'text-gray-500'}>
                        {formData.category || 'Select category'}
                      </span>
                      <ChevronDown 
                        size={16} 
                        className={`text-gray-400 transition-transform duration-200 ${isCategoryDropdownOpen ? 'rotate-180' : ''}`} 
                      />
                    </button>
                    
                    {isCategoryDropdownOpen && (
                      <div className="absolute top-full left-0 mt-2 w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl shadow-xl z-50 overflow-hidden">
                        {categories.map((category) => (
                          <button
                            key={category}
                            type="button"
                            onClick={() => handleCategorySelect(category)}
                            className={`w-full px-4 py-3 text-left transition-all duration-200 flex items-center justify-between ${
                              category === formData.category
                                ? 'bg-white/10 text-white'
                                : 'text-gray-300 hover:bg-white/5 hover:text-white'
                            }`}
                          >
                            <span>{category}</span>
                            {category === formData.category && (
                              <Check size={16} className="text-white" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {formData.category === 'Other' && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Custom Category <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={otherCategory}
                    onChange={(e) => setOtherCategory(e.target.value)}
                    maxLength={30}
                    className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200"
                    placeholder="Enter custom category"
                    required
                    autoComplete="off"
                    style={{ position: 'relative', zIndex: 10 }}
                  />
                  <p className="text-xs text-gray-400 text-right">
                    {otherCategory.length}/30 characters
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Color <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="color"
                  value={formData.color}
                  onChange={handleInputChange}
                  maxLength={50}
                  className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200"
                  placeholder="e.g., Red, Blue, Black, etc."
                  required
                  autoComplete="off"
                  style={{ position: 'relative', zIndex: 10 }}
                />
                <p className="text-xs text-gray-400 text-right">
                  {formData.color.length}/50 characters
                </p>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Description <span className="text-red-400">*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  maxLength={1000}
                  rows={4}
                  className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200 resize-none"
                  placeholder="Describe your piece in detail..."
                  required
                  autoComplete="off"
                  style={{ position: 'relative', zIndex: 10 }}
                />
                <p className="text-xs text-gray-400 text-right">
                  {formData.description.length}/1000 characters
                </p>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Tags
                </label>
                
                {/* Tags Display */}
                {pieceTags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {pieceTags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-white/20 text-white rounded-full text-sm"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="text-white hover:text-gray-300 transition-colors"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Tag Input */}
                <div className="flex gap-2">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={handleTagKeyPress}
                      maxLength={20}
                      placeholder={pieceTags.length >= 5 ? "Maximum 5 tags reached" : "Add a tag..."}
                      disabled={pieceTags.length >= 5}
                      className={`w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200 ${
                        pieceTags.length >= 5 ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      autoComplete="off"
                      style={{ position: 'relative', zIndex: 10 }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={addTag}
                    disabled={pieceTags.length >= 5}
                    className={`px-6 py-3 rounded-xl transition-all duration-200 font-medium ${
                      pieceTags.length >= 5 
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                        : 'bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white hover:scale-105'
                    }`}
                  >
                    Add
                  </button>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-400">
                    Add up to 5 tags to help categorize your piece (e.g., "vintage", "casual", "luxury")
                  </span>
                  {newTag.length > 0 && (
                    <span className="text-gray-400">
                      {newTag.length}/20
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Thumbnail Upload */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white border-b border-[#2a2a2a] pb-2">Thumbnail Image</h3>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Thumbnail Image <span className="text-red-400">*</span>
                </label>
                <p className="text-sm text-gray-400 mb-4">
                  Upload a high-quality image that represents your piece. This will be used as the main preview image.
                </p>
                
                <div className="border-2 border-dashed border-[#2a2a2a] rounded-2xl p-6 text-center transition-all duration-200 hover:border-white hover:bg-[#0a0a0a]">
                  {thumbnail ? (
                    <div className="space-y-4">
                      {/* Thumbnail Preview */}
                      <div className="flex justify-center">
                        <div className="relative">
                          <img
                            src={thumbnailPreview}
                            alt="Thumbnail preview"
                            className="w-32 h-32 object-cover rounded-xl border border-[#2a2a2a]"
                          />
                          <button
                            type="button"
                            onClick={removeThumbnail}
                            className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition-all duration-200 hover:scale-110"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                      
                      {/* Thumbnail Details */}
                      <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#2a2a2a]">
                        <div className="text-center">
                          <h4 className="text-white font-semibold text-sm mb-2">{thumbnail.name}</h4>
                          <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
                            <span>{(thumbnail.size / 1024 / 1024).toFixed(2)} MB</span>
                            <span>•</span>
                            <span>{thumbnail.type}</span>
                            <span>•</span>
                            <span className="text-green-400 font-medium">Ready</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Change Thumbnail Button */}
                      <button
                        type="button"
                        onClick={() => document.getElementById('thumbnail-upload').click()}
                        className="px-4 py-2 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 text-sm font-medium hover:scale-105"
                      >
                        Change Image
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto overflow-hidden">
                        <img 
                          src="/images/piece-placeholder.png" 
                          alt="Thumbnail Upload" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <p className="text-white font-semibold text-lg mb-2">Upload thumbnail image</p>
                        <p className="text-gray-400 text-sm mb-4 max-w-sm mx-auto">
                          Supports JPG, PNG, GIF, and WebP files up to 10MB. Recommended size: 512x512px for best quality.
                        </p>
                        <button
                          type="button"
                          onClick={() => document.getElementById('thumbnail-upload').click()}
                          className="px-6 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-medium hover:scale-105 shadow-lg hover:shadow-xl"
                        >
                          Choose Image
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <input
                    id="thumbnail-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleThumbnailUpload}
                    className="hidden"
                  />
                </div>
              </div>
            </div>

            {/* Pricing & Supply */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white border-b border-[#2a2a2a] pb-2">Pricing & Supply</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label htmlFor="price" className="block text-sm font-medium text-gray-300 mb-2">
                    Price (SOL) <span className="text-red-400">*</span>
                  </label>
                  <div className="space-y-2">
                    <input
                      type="number"
                      id="price"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      step="0.001"
                      min="0"
                      placeholder="0.1"
                      className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all"
                      autoComplete="off"
                      required
                      style={{ position: 'relative', zIndex: 10 }}
                    />
                    {/* USD Conversion Display */}
                    {formData.price && parseFloat(formData.price) > 0 && (
                      <div className="text-xs text-gray-400 text-center bg-[#1a1a1a] px-2 py-1 rounded border border-[#3a3a3a]">
                        ≈ ${(parseFloat(formData.price) * solPrice).toFixed(2)} USD
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label htmlFor="supply" className="block text-sm font-medium text-gray-300 mb-2">
                    Quantity <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="number"
                    id="supply"
                    name="supply"
                    value={formData.supply}
                    onChange={handleInputChange}
                    min="1"
                    max="50"
                    placeholder="1"
                    className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all"
                    autoComplete="off"
                    required
                    style={{ position: 'relative', zIndex: 10 }}
                  />
                  <p className="text-xs text-gray-400 mt-1">Maximum 50 pieces per creation</p>
                </div>
                <div>
                  <label htmlFor="royaltyPercent" className="block text-sm font-medium text-gray-300 mb-2">
                    Creator Royalties (%) <span className="text-red-400">*</span>
                  </label>
                  <div className="space-y-2">
                    <input
                      type="number"
                      id="royaltyPercent"
                      name="royaltyPercent"
                      value={formData.royaltyPercent}
                      onChange={handleInputChange}
                      step="0.1"
                      min="0"
                      max="10"
                      placeholder="0"
                      className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all"
                      autoComplete="off"
                      required
                      aria-label="Creator Royalties (%)"
                      style={{ position: 'relative', zIndex: 10 }}
                    />
                    {/* Validation Message */}
                    {formData.royaltyPercent && (Number(formData.royaltyPercent) < 0 || Number(formData.royaltyPercent) > 10) && (
                      <p className="text-red-500 text-xs">Max 10% royalties allowed.</p>
                    )}
                    {formData.royaltyPercent && Number(formData.royaltyPercent) >= 0 && Number(formData.royaltyPercent) <= 10 && (
                      <p className="text-green-500 text-xs">{formData.royaltyPercent}% royalty on resales</p>
                    )}
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-400">
                Royalties are paid to you automatically on every resale (0-10% maximum).
              </p>
            </div>

            {/* Connected Wallet Info */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white border-b border-[#2a2a2a] pb-2">Payment Details</h3>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Payout Wallet Address
                </label>
                <div className="px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white">
                  {publicKey ? (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-mono">
                        {publicKey.toString().slice(0, 8)}...{publicKey.toString().slice(-8)}
                      </span>
                      <span className="text-xs text-green-400 bg-green-400/20 px-2 py-1 rounded-full">
                        Connected
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-500">Please connect your wallet</span>
                  )}
                </div>
                <p className="text-xs text-gray-400">
                  Payments will be sent to your connected wallet address
                </p>
              </div>
            </div>

            {/* Social Links */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white border-b border-[#2a2a2a] pb-2">Social Links</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Website <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    maxLength={200}
                    className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200"
                    placeholder="https://yourwebsite.com"
                    autoComplete="off"
                    style={{ position: 'relative', zIndex: 10 }}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    X (Twitter) <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="url"
                    name="twitter"
                    value={formData.twitter}
                    onChange={handleInputChange}
                    maxLength={200}
                    className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200"
                    placeholder="https://x.com/username"
                    autoComplete="off"
                    style={{ position: 'relative', zIndex: 10 }}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Instagram <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="url"
                    name="instagram"
                    value={formData.instagram}
                    onChange={handleInputChange}
                    maxLength={200}
                    className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent transition-all duration-200"
                    placeholder="https://instagram.com/username"
                    autoComplete="off"
                    style={{ position: 'relative', zIndex: 10 }}
                  />
                </div>
              </div>
            </div>

            {/* 3D Model Upload */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white border-b border-[#2a2a2a] pb-2">3D Model (Required)</h3>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  3D Model File <span className="text-red-400">*</span>
                </label>
                <div 
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={handleUploadAreaClick}
                  className={`border-2 border-dashed border-[#2a2a2a] rounded-2xl p-8 text-center transition-all duration-200 ${
                    pieceModel 
                      ? 'cursor-default' 
                      : 'hover:border-white hover:bg-[#0a0a0a] cursor-pointer'
                  }`}
                >
                  {pieceModel ? (
                    <div className="space-y-6">
                      {/* 3D Model Preview */}
                      <div className="space-y-4">
                        <h4 className="text-white font-medium text-center">3D Model Preview</h4>
                        <div className="flex justify-center">
                          <ModelViewer key={modelViewerKey} file={pieceModel} />
                        </div>
                      </div>
                      
                      {/* File Preview Header */}
                      <div className="bg-[#0a0a0a] rounded-xl p-6 border border-[#2a2a2a]">
                        <div className="mb-4">
                          <h3 className="text-white font-semibold text-lg mb-2">{pieceModel.name}</h3>
                          <div className="flex items-center gap-4 text-sm text-gray-400">
                            <span className="flex items-center gap-1">
                              <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                              {filePreview?.extension || 'GLB'} File
                            </span>
                            <span>{(pieceModel.size / 1024 / 1024).toFixed(2)} MB</span>
                          </div>
                        </div>
                        
                        {/* File Details Grid */}
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-gray-400">File Type:</span>
                              <span className="text-white">{filePreview?.type || 'Unknown'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-400">Extension:</span>
                              <span className="text-white">{filePreview?.extension || 'GLB'}</span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-gray-400">Size:</span>
                              <span className="text-white">{(pieceModel.size / 1024 / 1024).toFixed(2)} MB</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-400">Status:</span>
                              <span className="text-green-400 font-medium">Ready for Preview</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-3 justify-center">
                        <button
                          type="button"
                          onClick={triggerFileUpload}
                          className="px-4 py-2 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 text-sm font-medium hover:scale-105"
                        >
                          Change File
                        </button>
                        <button
                          type="button"
                          onClick={removeModel}
                          className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl transition-all duration-200 text-sm font-medium hover:scale-105 flex items-center gap-2"
                        >
                          <Trash2 size={16} />
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto overflow-hidden">
                        <img 
                          src="/images/piece-placeholder.png" 
                          alt="3D Model Upload" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <p className="text-white font-semibold text-lg mb-2">Upload your 3D model</p>
                        <p className="text-gray-400 text-sm mb-6 max-w-sm mx-auto">
                          <span className="text-white font-semibold">Only GLB files accepted</span> (up to 50MB)<br/>
                          Drag and drop or click to browse.
                        </p>
                        <button
                          type="button"
                          onClick={triggerFileUpload}
                          className="px-8 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl transition-all duration-200 font-medium hover:scale-105 shadow-lg hover:shadow-xl"
                        >
                          Choose File
                        </button>
                      </div>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".glb,model/gltf-binary"
                    onChange={handleFileUpload}
                    className="hidden"
                    title="Only GLB files are supported"
                  />
                </div>
              </div>
            </div>

            {/* Fee Estimation */}
            {formData.supply && parseInt(formData.supply) > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white border-b border-[#2a2a2a] pb-2">Estimated Minting Fees</h3>
                
                <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] rounded-2xl p-6 border border-[#2a2a2a]">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center">
                      <Zap className="text-blue-400" size={24} />
                    </div>
                    
                    <div className="flex-1 space-y-4">
                      <div>
                        <h4 className="text-white font-semibold mb-1">Wallet Transaction Fees</h4>
                        <p className="text-gray-400 text-sm">
                          You'll pay these fees directly from your wallet when minting. These cover blockchain transaction costs and account creation.
                        </p>
                      </div>
                      
                      {/* Fee Breakdown */}
                      <div className="space-y-2">
                        <div className="flex justify-between items-center py-2 border-b border-[#2a2a2a]">
                          <span className="text-gray-300 text-sm">Pieces to mint</span>
                          <span className="text-white font-medium">{parseInt(formData.supply)}</span>
                        </div>
                        
                        <div className="flex justify-between items-center py-2 border-b border-[#2a2a2a]">
                          <span className="text-gray-300 text-sm">Cost per piece</span>
                          <div className="text-right">
                            <div className="text-white font-medium">~0.004 SOL</div>
                            <div className="text-xs text-gray-400">
                              ≈ ${(0.004 * solPrice).toFixed(2)} USD
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex justify-between items-center py-3 bg-[#0a0a0a] rounded-xl px-4 mt-3">
                          <span className="text-white font-semibold">Estimated Total</span>
                          <div className="text-right">
                            <div className="text-white font-bold text-lg">
                              ~{(parseInt(formData.supply) * 0.004).toFixed(4)} SOL
                            </div>
                            <div className="text-sm text-gray-400">
                              ≈ ${(parseInt(formData.supply) * 0.004 * solPrice).toFixed(2)} USD
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Info Message */}
                      <div className="flex items-start gap-2 bg-blue-500/5 border border-blue-500/20 rounded-xl p-3">
                        <AlertTriangle className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
                        <p className="text-blue-300 text-xs">
                          This is an estimate. Actual fees may vary slightly based on network conditions. 
                          Includes transaction fees, token account creation, and metadata storage.
                        </p>
                      </div>
                      
                      {/* Wallet Balance Check */}
                      {walletBalance !== null && (() => {
                        const totalFee = parseInt(formData.supply) * 0.004;
                        const hasInsufficientBalance = walletBalance < totalFee;
                        
                        return (
                          <div className={`flex items-start gap-2 rounded-xl p-3 ${
                            hasInsufficientBalance 
                              ? 'bg-red-500/10 border border-red-500/30' 
                              : 'bg-green-500/10 border border-green-500/30'
                          }`}>
                            {hasInsufficientBalance ? (
                              <XCircle className="text-red-400 flex-shrink-0 mt-0.5" size={16} />
                            ) : (
                              <CheckCircle className="text-green-400 flex-shrink-0 mt-0.5" size={16} />
                            )}
                            <div className="flex-1">
                              <p className={`text-xs ${hasInsufficientBalance ? 'text-red-300' : 'text-green-300'}`}>
                                {hasInsufficientBalance ? (
                                  <>
                                    <span className="font-semibold">Insufficient balance.</span> You have {walletBalance.toFixed(4)} SOL but need {totalFee.toFixed(4)} SOL to mint. The mint button is disabled until you add more SOL to your wallet.
                                  </>
                                ) : (
                                  <>
                                    <span className="font-semibold">Balance OK.</span> You have {walletBalance.toFixed(4)} SOL available ({(walletBalance - totalFee).toFixed(4)} SOL remaining after minting).
                                  </>
                                )}
                              </p>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end items-center pt-8 border-t border-[#2a2a2a]">
              <button
                type="submit"
                disabled={
                  isCreating || 
                  !pieceModel ||
                  !thumbnail ||
                  !formData.name ||
                  !formData.description ||
                  !formData.category ||
                  !formData.color ||
                  !formData.price ||
                  !formData.supply ||
                  !formData.website ||
                  !formData.twitter ||
                  !formData.instagram ||
                  (formData.royaltyPercent && (Number(formData.royaltyPercent) < 0 || Number(formData.royaltyPercent) > 10)) ||
                  (walletBalance !== null && formData.supply && walletBalance < (parseInt(formData.supply) * 0.004))
                }
                className="px-10 py-4 bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 active:from-blue-700 active:to-blue-800 border border-blue-400/30 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-xl transition-all duration-200 font-semibold text-lg flex items-center gap-3 hover:scale-105 shadow-lg hover:shadow-xl hover:shadow-blue-500/25 disabled:hover:scale-100 disabled:shadow-none"
              >
                {isCreating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                    Creating & Minting...
                  </>
                ) : (
                  <div className="flex items-center gap-3">
                    <Plus size={20} />
                    Mint Piece
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Mint Progress Modal (like execute trading page) */}
      {mintProgressModal.show && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-[80] p-4">
          <div className="bg-[#111111] border-2 border-gray-600/50 rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl shadow-gray-500/10">
            {!mintProgressModal.error && !mintProgressModal.isComplete ? (
              <>
                {/* Loading State */}
                <div className="p-8">
                  <div className="flex flex-col items-center">
                    {/* Animated 3D Logo */}
                    <div className="relative w-24 h-24 mb-6">
                      <Logo3D 
                        className="w-full h-full"
                        autoRotate={true}
                        baseRotationSpeed={0.03}
                        enableControls={false}
                        showEnvironment={true}
                        cameraPosition={[0, 0, 3.5]}
                        fov={50}
                        loadingMode={true}
                      />
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-2xl font-bold text-white mb-2">Minting Piece</h3>
                    <p className="text-gray-400 text-center mb-6">{mintProgressModal.currentMessage}</p>
                    
                    {/* Progress Steps */}
                    <div className="w-full max-w-md space-y-3 mb-6">
                      {[
                        { step: 1, label: 'Creating Metadata' },
                        { step: 2, label: 'Minting NFT on Blockchain' }
                      ].map(({ step, label }) => (
                        <div key={step} className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                            mintProgressModal.step > step 
                              ? 'bg-green-500/20 border-2 border-green-500' 
                              : mintProgressModal.step === step 
                              ? 'bg-gradient-to-r from-gray-300/20 to-gray-100/20 border-2 border-gray-300'
                              : 'bg-[#2a2a2a] border-2 border-[#2a2a2a]'
                          }`}>
                            {mintProgressModal.step > step ? (
                              <CheckCircle size={16} className="text-green-400" />
                            ) : mintProgressModal.step === step ? (
                              <div className="w-3 h-3 bg-gradient-to-r from-gray-200 to-gray-400 rounded-full animate-pulse"></div>
                            ) : (
                              <span className="text-gray-600 text-xs font-bold">{step}</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className={`text-sm font-medium transition-colors ${
                              mintProgressModal.step >= step ? 'text-white' : 'text-gray-600'
                            }`}>
                              {label}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Loading Bar */}
                    <div className="w-full bg-[#2a2a2a] rounded-full h-2 overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-gray-300 via-gray-100 to-gray-400 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${(mintProgressModal.step / mintProgressModal.totalSteps) * 100}%` }}
                      ></div>
                    </div>
                    
                    {/* Info Text */}
                    <p className="text-gray-500 text-xs mt-4 text-center">
                      Please don't close this window. This may take a few moments...
                    </p>
                  </div>
                </div>
              </>
            ) : mintProgressModal.isComplete ? (
              <>
                {/* Success State */}
                <div className="p-8">
                  <div className="flex flex-col items-center">
                    {/* Success Icon */}
                    <div className="relative w-24 h-24 mb-6">
                      <div className="absolute inset-0 bg-gradient-to-r from-gray-300 via-gray-100 to-gray-400 rounded-full opacity-30"></div>
                      <div className="absolute inset-2 bg-[#111111] rounded-full flex items-center justify-center">
                        <CheckCircle size={48} className="text-green-400" />
                      </div>
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-2xl font-bold text-white mb-2">Minting Complete! 🎉</h3>
                    <p className="text-gray-400 text-center mb-6">
                      Your piece has been successfully minted on Solana
                    </p>
                    
                    {/* Transaction Details */}
                    <div className="w-full bg-[#0a0a0a] rounded-xl p-4 mb-6 space-y-3">
                      {mintProgressModal.mintAddress && (
                        <div className="flex items-start justify-between gap-3">
                          <span className="text-gray-400 text-sm">Mint Address</span>
                          <span className="text-white font-mono text-xs truncate max-w-[200px]">
                            {mintProgressModal.mintAddress}
                          </span>
                        </div>
                      )}
                      
                      {mintProgressModal.mintSignature && (
                        <div className="flex items-start justify-between gap-3">
                          <span className="text-gray-400 text-sm">Transaction</span>
                          <a
                            href={getExplorerUrl(mintProgressModal.mintSignature, 'mainnet-beta')}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-300 hover:text-white text-sm font-mono truncate max-w-[200px] transition-colors"
                          >
                            {mintProgressModal.mintSignature.substring(0, 8)}...
                          </a>
                        </div>
                      )}
                    </div>
                    
                    {/* Action Button */}
                    <button
                      onClick={() => {
                        setMintProgressModal({ ...mintProgressModal, show: false });
                        navigate('/profile/created');
                      }}
                      className="w-full px-6 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl font-medium transition-colors"
                    >
                      View My Pieces
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Error State */}
                <div className="p-8">
                  <div className="flex flex-col items-center">
                    {/* Error Icon */}
                    <div className="relative w-24 h-24 mb-6">
                      <div className="absolute inset-0 bg-gradient-to-r from-gray-400 to-gray-500 rounded-full opacity-20"></div>
                      <div className="absolute inset-2 bg-[#111111] rounded-full flex items-center justify-center">
                        <XCircle size={48} className="text-red-400" />
                      </div>
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-2xl font-bold text-white mb-2">Minting Failed</h3>
                    <p className="text-gray-400 text-center mb-6">
                      {mintProgressModal.error || 'An error occurred while minting your piece'}
                    </p>
                    
                    {/* Action Button */}
                    <button
                      onClick={() => setMintProgressModal({ ...mintProgressModal, show: false })}
                      className="w-full px-6 py-3 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-white rounded-xl font-medium transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && mintResult && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-black border border-white/20 rounded-2xl max-w-md w-full p-6 shadow-2xl" style={{backgroundColor: '#000000', opacity: 1}}>
            {/* Header */}
            <div className="text-center mb-6">
              <div className="flex items-center justify-center mx-auto mb-4">
                <img src="/images/piece-placeholder.png" alt="Logo" className="w-20 h-20" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">🎉 Success!</h2>
              <p className="text-sm text-white/70">
                Your NFT has been created and minted successfully
              </p>
            </div>

            {/* Mint Details */}
            <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-green-400 font-semibold mb-2 text-sm">Minted on Solana!</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-white/60">Mint Address:</span>
                    </div>
                    <code className="text-xs bg-white/5 text-green-400 px-3 py-2 rounded font-mono block break-all">
                      {mintResult.mintAddress}
                    </code>
                    {mintResult.explorerUrl && (
                      <a 
                        href={mintResult.explorerUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-400 hover:text-blue-300 underline flex items-center gap-1"
                      >
                        View on Solana Explorer →
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Button */}
            <button
              onClick={handleCloseSuccessModal}
              className="w-full text-white font-medium py-3 px-4 transition-all duration-200 hover:scale-105 cursor-pointer flex items-center justify-center gap-2"
            >
              View My Pieces <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Minting Error Modal */}
      {showMintErrorModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-500/30 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-b border-yellow-500/30 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Partial Minting Failure</h3>
                    <p className="text-sm text-gray-400">Some pieces failed to mint</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowMintErrorModal(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-200px)]">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <div className="text-gray-400 text-sm mb-1">Requested</div>
                  <div className="text-2xl font-bold text-white">{mintErrorDetails.quantity}</div>
                </div>
                <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/30">
                  <div className="text-gray-400 text-sm mb-1">Successful</div>
                  <div className="text-2xl font-bold text-green-400">{mintErrorDetails.totalMinted}</div>
                </div>
                <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/30">
                  <div className="text-gray-400 text-sm mb-1">Failed</div>
                  <div className="text-2xl font-bold text-red-400">{mintErrorDetails.totalFailed}</div>
                </div>
              </div>

              {/* Error Details */}
              <div className="mb-6">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <span className="w-1 h-5 bg-yellow-500 rounded"></span>
                  Error Details
                </h4>
                <div className="bg-gray-800/50 rounded-lg border border-gray-700 divide-y divide-gray-700 max-h-60 overflow-y-auto">
                  {mintErrorDetails.errors && mintErrorDetails.errors.length > 0 ? (
                    mintErrorDetails.errors.map((error, index) => (
                      <div key={index} className="p-4 hover:bg-gray-800/80 transition-colors">
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <XCircle className="w-4 h-4 text-red-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-white font-medium mb-1">Piece {error.pieceNumber}</div>
                            <div className="text-sm text-gray-400 break-words">{error.error}</div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-4 text-gray-400 text-sm">
                      Some pieces failed to mint due to network or transaction errors.
                    </div>
                  )}
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-white font-medium mb-1">What should I do?</div>
                    <p className="text-sm text-gray-300 mb-2">
                      Please try creating the piece again to mint all {mintErrorDetails.quantity} pieces.
                      The system will skip already minted pieces and only mint the failed ones.
                    </p>
                    <p className="text-xs text-gray-400">
                      Common causes: Insufficient SOL balance, network congestion, or wallet transaction rejection.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-700 px-6 py-4 bg-gray-900/50">
              <button
                onClick={() => setShowMintErrorModal(false)}
                className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-semibold py-3 rounded-lg hover:from-yellow-600 hover:to-orange-600 transition-all duration-200"
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification.show && (
        <div className="fixed top-4 right-4 z-[70] animate-slide-down">
          <div className={`${
            notification.type === 'success' ? 'bg-green-500/20 border-green-500/50' :
            notification.type === 'error' ? 'bg-red-500/20 border-red-500/50' :
            'bg-blue-500/20 border-blue-500/50'
          } border rounded-xl p-4 shadow-xl max-w-md`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                notification.type === 'success' ? 'bg-green-500/20' :
                notification.type === 'error' ? 'bg-red-500/20' :
                'bg-blue-500/20'
              }`}>
                {notification.type === 'success' ? (
                  <Check className="w-5 h-5 text-green-400" />
                ) : notification.type === 'error' ? (
                  <X className="w-5 h-5 text-red-400" />
                ) : (
                  <Eye className="w-5 h-5 text-blue-400" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-white text-sm">{notification.message}</p>
              </div>
              <button
                onClick={() => setNotification({ ...notification, show: false })}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
    </WalletRequired>
  );
};

export default CreatePiece; 